/**
 * Smith Chart Utility Functions
 * Converts S-parameter data to format required by Plotly scattersmith
 */

/**
 * Convert magnitude (dB) and phase (degrees) to complex number
 * @param {number} magDb - Magnitude in dB
 * @param {number} phaseDeg - Phase in degrees
 * @returns {{real: number, imag: number}} Complex number
 */
export function dbPhaseToComplex(magDb, phaseDeg) {
  // Convert dB to linear magnitude
  const magLinear = Math.pow(10, magDb / 20);

  // Convert degrees to radians
  const phaseRad = (phaseDeg * Math.PI) / 180;

  // Convert polar to rectangular
  return {
    real: magLinear * Math.cos(phaseRad),
    imag: magLinear * Math.sin(phaseRad)
  };
}

/**
 * Convert reflection coefficient (Gamma) to normalized impedance
 * Z_norm = (1 + Gamma) / (1 - Gamma)
 * @param {{real: number, imag: number}} gamma - Complex reflection coefficient
 * @returns {{real: number, imag: number}} Normalized impedance
 */
export function gammaToImpedance(gamma) {
  // Z = (1 + Gamma) / (1 - Gamma)
  // Using complex division: (a + bi) / (c + di) = ((ac + bd) + (bc - ad)i) / (c² + d²)

  const numeratorReal = 1 + gamma.real;
  const numeratorImag = gamma.imag;
  const denominatorReal = 1 - gamma.real;
  const denominatorImag = -gamma.imag;

  const denomMagSquared = denominatorReal * denominatorReal + denominatorImag * denominatorImag;

  // Avoid division by zero (when Gamma = 1, which means open circuit)
  if (denomMagSquared < 1e-10) {
    return { real: 1000, imag: 0 }; // Return large impedance for open circuit
  }

  return {
    real: (numeratorReal * denominatorReal + numeratorImag * denominatorImag) / denomMagSquared,
    imag: (numeratorImag * denominatorReal - numeratorReal * denominatorImag) / denomMagSquared
  };
}

/**
 * Convert S-parameter array (dB magnitude + phase) to Plotly scattersmith format
 * @param {number[]} magDbArray - Array of magnitudes in dB
 * @param {number[]} phaseDegArray - Array of phases in degrees
 * @returns {{real: number[], imag: number[]}} Arrays for Plotly scattersmith
 */
export function sParamToSmithData(magDbArray, phaseDegArray) {
  const realValues = [];
  const imagValues = [];

  for (let i = 0; i < magDbArray.length; i++) {
    const gamma = dbPhaseToComplex(magDbArray[i], phaseDegArray[i]);
    const z = gammaToImpedance(gamma);
    realValues.push(z.real);
    imagValues.push(z.imag);
  }

  return { real: realValues, imag: imagValues };
}

/**
 * Calculate VSWR from reflection coefficient magnitude
 * @param {number} gammaMag - Magnitude of reflection coefficient (linear, not dB)
 * @returns {number} VSWR value
 */
export function gammaToVSWR(gammaMag) {
  if (gammaMag >= 1) return Infinity;
  if (gammaMag < 0) gammaMag = 0;
  return (1 + gammaMag) / (1 - gammaMag);
}

/**
 * Calculate return loss from reflection coefficient magnitude in dB
 * @param {number} gammaDb - Magnitude of reflection coefficient in dB
 * @returns {number} Return loss in dB (positive value)
 */
export function gammaDbToReturnLoss(gammaDb) {
  return -gammaDb;
}

/**
 * Convert normalized impedance to actual impedance
 * @param {{real: number, imag: number}} zNorm - Normalized impedance
 * @param {number} z0 - Reference impedance (typically 50 ohms)
 * @returns {{resistance: number, reactance: number}} Actual impedance in ohms
 */
export function normalizedToActualImpedance(zNorm, z0 = 50) {
  return {
    resistance: zNorm.real * z0,
    reactance: zNorm.imag * z0
  };
}

/**
 * Format impedance for display
 * @param {number} resistance - Resistance in ohms
 * @param {number} reactance - Reactance in ohms
 * @returns {string} Formatted impedance string like "50 + j25 Ω"
 */
export function formatImpedance(resistance, reactance) {
  const rStr = resistance.toFixed(1);
  const sign = reactance >= 0 ? '+' : '-';
  const xStr = Math.abs(reactance).toFixed(1);
  return `${rStr} ${sign} j${xStr} Ω`;
}

/**
 * S-parameter trace colors matching Microsoft example
 */
export const S_PARAM_COLORS = {
  S11: '#FFD700', // Yellow/Gold
  S12: '#00FF00', // Green
  S21: '#FF0000', // Red
  S22: '#00FFFF'  // Cyan
};
